import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../components/ui';
import { translate } from './i18n';

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_STYLES: Record<ToastTone, { className: string; icon: ReactNode }> = {
  success: {
    className: 'border-good/30 bg-good-soft text-good',
    icon: <CheckCircle2 size={16} />,
  },
  error: {
    className: 'border-danger/30 bg-danger-soft text-danger',
    icon: <AlertTriangle size={16} />,
  },
  info: {
    className: 'border-line bg-surface text-ink',
    icon: <Info size={16} />,
  },
};

const DISMISS_AFTER_MS = 5_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone, message }]);
      setTimeout(() => dismiss(id), DISMISS_AFTER_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Bottom on phones (near the thumb), top-right on wider screens. */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-3 bottom-3 z-50 flex flex-col gap-2 sm:inset-x-auto sm:top-4 sm:right-4 sm:bottom-auto sm:w-96"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm shadow-lg',
              TONE_STYLES[toast.tone].className,
            )}
          >
            <span className="mt-0.5 shrink-0">
              {TONE_STYLES[toast.tone].icon}
            </span>
            <span className="min-w-0 flex-1 break-anywhere">
              {toast.message}
            </span>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label={translate('common.dismiss')}
              className="shrink-0 opacity-60 hover:opacity-100"
            >
              <X size={15} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside a ToastProvider');
  return api;
}

/** Pulls a readable message out of whatever a mutation rejected with. */
export function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : translate('common.somethingWentWrong');
}
